// Shell do Painel Admin (/manager/). Espelha a estrutura visual do
// /student/ — sidebar fixa em desktop, drawer em mobile, breadcrumbs no
// topo, área principal renderizando a seção ativa.
//
// Auth: chama /api/auth/me no bootstrap. Se 401/403 ou role != admin, manda
// pra /auth/. Sem login inline aqui (admin loga pelo auth-new como
// qualquer outro role e escolhe contexto).
//
// Seções (replicam abas do manager legado):
//   - Usuários, Professores, Grupos, Bancos, Recado, Jogos, Analytics
// Cada uma é um <ComingSoonSection /> por enquanto — vamos implementar
// section-by-section nas próximas iterações.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowLeftRight,
  BarChart3,
  Bell,
  Brain,
  Building2,
  Coins,
  CreditCard,
  Crown,
  Activity,
  Flag as FlagIcon,
  ChevronDown,
  ChevronRight,
  Database,
  Eye,
  Gamepad2,
  LayoutTemplate,
  Library,
  Link2,
  Loader2,
  LogOut,
  Megaphone,
  Menu,
  Moon,
  HelpCircle,
  FileText,
  Bug,
  DoorOpen,
  Mail,
  MessageSquareWarning,
  Music,
  PanelLeftClose,
  PanelLeftOpen,
  Palette,
  PenSquare,
  Repeat,
  GraduationCap,
  Settings,
  Gift,
  Shield,
  ShieldQuestion,
  Sparkles,
  Sun,
  Users,
  UserCog,
  UserPlus,
  UsersRound,
  Puzzle,
  Wallet,
  X
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { getActiveTheme, toggleTheme } from "@app/ui/lib/theme";
import { api, ApiError, type UserSession, type ActiveContext } from "./api";
import { RecadoView } from "./RecadoView";
import { JogosConfigView } from "./JogosConfigView";
import { OrphanDefaultView } from "./OrphanDefaultView";
import { StudentUiVisibilityView } from "./StudentUiVisibilityView";
import { AiCreditsGateView } from "./AiCreditsGateView";
import { QuickStudyConfigView } from "./QuickStudyConfigView";
import { QuickStudyIncidentsView } from "./QuickStudyIncidentsView";
import { PaymentSandboxView } from "./PaymentSandboxView";
import { BillingView } from "./BillingView";
import { CreditsView } from "./CreditsView";
import { AiStudyAccessView } from "./AiStudyAccessView";
import { ConfirmRoot, PromptRoot, openAlert } from "./ConfirmPromptModal";
import { PlayerStationsView } from "./PlayerStationsView";
import { BancosView } from "./BancosView";
import { EmailLogView } from "./EmailLogView";
import { LibraryExplorerView } from "./LibraryExplorerView";
import { UsuariosView } from "./UsuariosView";
import { ReferralsView } from "./ReferralsView";
import { ProfessoresView } from "./ProfessoresView";
import { GruposView } from "./GruposView";
import { GruposEstudoView } from "./GruposEstudoView";
import { OrganizacoesView } from "./OrganizacoesView";
import { AiConnectionsConfigView } from "./AiConnectionsConfigView";
import { SignupAllowlistView } from "./SignupAllowlistView";
import { PaymentsConfigView } from "./PaymentsConfigView";
import { AssinaturasView } from "./AssinaturasView";
import { PlanosView } from "./PlanosView";
import { CreditsPlansView } from "./CreditsPlansView";
import { AdminAlertsView } from "./AdminAlertsView";
import { EmailTemplatesView } from "./EmailTemplatesView";
import { EmailSettingsView } from "./EmailSettingsView";
import { FeatureFlagsView } from "./FeatureFlagsView";
import { AiFeatureConfigView } from "./AiFeatureConfigView";
import { AiUsersStatusView } from "./AiUsersStatusView";
import { AiUsageView } from "./AiUsageView";
import { SidebarDefaultsView } from "./SidebarDefaultsView";
import { NotificationsCatalogView } from "./NotificationsCatalogView";
import { UserReportsView } from "./UserReportsView";
import { FaqView } from "./FaqView";
import { EssayThemesView } from "./EssayThemesView";
import { BrandingView } from "./BrandingView";
import { LegalTermsView } from "./LegalTermsView";
import { ErrorLogView } from "./ErrorLogView";
import { AccountClosuresView } from "./AccountClosuresView";
import { ExtensionAccessView } from "./ExtensionAccessView";
import { bootstrapFeatureFlags } from "@app/ui/lib/feature-flags";
import { friendlyError } from "@app/ui/lib/friendly-error";

// =====================
// Nav + state
// =====================

type SectionKey =
  | "organizacoes"
  | "usuarios"
  | "signup"
  | "professores"
  | "grupos"
  | "gruposestudo"
  | "bancos"
  | "library"
  | "essaythemes"
  | "recado"
  | "jogos"
  | "billing"
  | "credits"
  | "creditsplans"
  | "adminalerts"
  | "player"
  | "aiconnections"
  | "aistudy"
  | "aifeatureconfig"
  | "aiusersstatus"
  | "aiusage"
  | "sidebardefaults"
  | "notifications"
  | "userreports"
  | "faq"
  | "branding"
  | "legal"
  | "errorlog"
  | "accountclosures"
  | "extensionaccess"
  | "payments"
  | "planos"
  | "assinaturas"
  | "emailsettings"
  | "emailtemplates"
  | "emaillog"
  | "featureflags"
  | "orphandefault"
  | "studentvisibility"
  | "aicreditsgate"
  | "quickstudyconfig"
  | "quickstudyincidents"
  | "paymentsandbox"
  | "analytics";

interface SectionMeta {
  key: SectionKey;
  label: string;
  icon: typeof Users;
  description: string;
  /** Grupo visual no sidebar — separa items em blocos com header
   *  (ex.: "Pessoas", "IA", "Configurações"). Itens sem grupo entram em
   *  "Geral". A ordem dos grupos no sidebar segue a ordem da primeira
   *  ocorrência de cada grupo em SECTIONS. */
  group?: SectionGroup;
  /** Sub-níveis opcionais. [[project-sidebar-submenus-backlog]] */
  children?: Array<{ key: SectionKey; label: string; icon?: typeof Users }>;
}

type SectionGroup =
  | "Pessoas & Conteúdo"
  | "Comunicação"
  | "IA"
  | "Pagamentos"
  | "Configurações"
  | "Operação";

function sectionLabel(section: SectionMeta | undefined): string {
  if (!section) return "—";
  if (section.key === "aiconnections") return "Conexoes IA";
  return section.label;
}

function sectionDescription(section: SectionMeta | undefined): string {
  if (!section) return "";
  if (section.key === "aiconnections") {
    return "Configura a experiencia de Conectar Google para Gemini";
  }
  return section.description;
}

const SECTIONS: SectionMeta[] = [
  {
    key: "organizacoes",
    label: "Organizações",
    icon: Building2,
    description: "Instituições e profissionais autônomos — admins, professores, alunos",
    group: "Pessoas & Conteúdo"
  },
  {
    key: "usuarios",
    label: "Usuários",
    icon: Users,
    description: "CRUD de alunos/professores/admins, tags, entitlements, passkeys",
    group: "Pessoas & Conteúdo"
  },
  {
    key: "referrals",
    label: "Indicações",
    icon: Gift,
    description: "Consolidado de indicações + drill-down por indicador",
    group: "Pessoas & Conteúdo"
  },
  {
    key: "signup",
    label: "Convites (signup)",
    icon: UserPlus,
    description: "Allowlist de emails autorizados a se cadastrar via /signup",
    group: "Pessoas & Conteúdo"
  },
  {
    key: "professores",
    label: "Professores",
    icon: UserCog,
    description: "Visão dos professores, bancos atribuídos, atividade",
    group: "Pessoas & Conteúdo"
  },
  {
    key: "grupos",
    label: "Turmas",
    icon: GraduationCap,
    description: "Turmas atreladas a disciplinas, com membros e bancos coletivos",
    group: "Pessoas & Conteúdo"
  },
  {
    key: "gruposestudo",
    label: "Grupos de Estudo",
    icon: Users,
    description: "Grupos peer-led criados pelos alunos (cross-org)",
    group: "Pessoas & Conteúdo"
  },
  {
    key: "bancos",
    label: "Bancos",
    icon: Database,
    description: "Catálogo de bancos, edição, limpeza de órfãs",
    group: "Pessoas & Conteúdo"
  },
  {
    key: "library",
    label: "Biblioteca de provas",
    icon: Library,
    description: "Pastas curadas para organizar o catálogo público que aparece para os alunos em Biblioteca de provas",
    group: "Pessoas & Conteúdo"
  },
  {
    key: "essaythemes",
    label: "Biblioteca de Redação",
    icon: PenSquare,
    description: "Temas de redação reutilizáveis (FUVEST, ENEM, UNICAMP, etc.) que o aluno pode aplicar no Modo Estudo",
    group: "Pessoas & Conteúdo"
  },
  {
    key: "recado",
    label: "Recado",
    icon: Megaphone,
    description: "Anúncio exibido na home, com tons e audiência",
    group: "Comunicação"
  },
  {
    key: "jogos",
    label: "Jogos",
    icon: Gamepad2,
    description: "Configuração e enable/disable dos jogos",
    group: "Pessoas & Conteúdo"
  },
  {
    key: "billing",
    label: "Billing",
    icon: CreditCard,
    description: "Planos, assinaturas e faturas dos usuários",
    group: "Pagamentos"
  },
  {
    key: "paymentsandbox",
    label: "Sandbox MP (testes)",
    icon: CreditCard,
    description: "Geração de Pix e captura de cartão via Mercado Pago, amarrado ao email do admin. Pra validar a integração antes de soltar pra alunos. Beta: ninguém é cobrado em prod ainda",
    group: "Pagamentos"
  },
  {
    key: "credits",
    label: "Créditos (pré-pago)",
    icon: Wallet,
    description: "Saldo pré-pago de cada usuário. Aluno paga Pix → admin libera créditos manualmente. Também concede créditos iniciais para novos cadastros",
    group: "Pagamentos"
  },
  {
    key: "creditsplans",
    label: "Créditos & Planos",
    icon: Coins,
    description: "Sistema de créditos novo (migration 0049): planos cobram em créditos, faixas R$→créditos com bônus, custo por feature. Beta Testers é hidden e tem consumo=0×",
    group: "Pagamentos"
  },
  {
    key: "player",
    label: "Player de Música",
    icon: Music,
    description: "Rádios disponíveis no player — nome, categoria, URL, ordem",
    group: "Configurações"
  },
  {
    key: "aiconnections",
    label: "Conexões IA",
    icon: Link2,
    description: "Configura a experiência de Conectar Google para Gemini",
    group: "IA"
  },
  {
    key: "aistudy",
    label: "Estudar com IA",
    icon: Sparkles,
    description: "Libera acesso à feature 'Estudando com IA' por usuário (piloto)",
    group: "IA"
  },
  {
    key: "aifeatureconfig",
    label: "Modelos IA por feature",
    icon: Brain,
    description: "Quando aluno não tem BYOK, qual provider+modelo+chave a plataforma usa em cada feature",
    group: "IA"
  },
  {
    key: "aiusersstatus",
    label: "IA por usuário",
    icon: Activity,
    description: "Quem tem Google/Gemini conectado, BYOK em uso e qual modelo rodou na última chamada — pra follow-up",
    group: "IA"
  },
  {
    key: "aiusage",
    label: "Consumo de tokens",
    icon: Coins,
    description: "Quantos tokens cada usuário consumiu — separa o que veio da plataforma (pago) do BYOK (chave do aluno). Base pra plano pós-pago",
    group: "IA"
  },
  {
    key: "aicreditsgate",
    label: "Créditos por feature",
    icon: Coins,
    description: "Liga/desliga verificação de saldo de créditos por feature de IA. Default OFF (tudo livre). Útil pra começar a cobrar uma feature por vez sem mexer no código",
    group: "IA"
  },
  {
    key: "quickstudyconfig",
    label: "Tira-dúvida (limites)",
    icon: Sparkles,
    description: "Limites de imagens (qtd e tamanho) e texto do tira-dúvida rápido + master switch da feature",
    group: "IA"
  },
  {
    key: "quickstudyincidents",
    label: "Tira-dúvida (incidentes)",
    icon: ShieldQuestion,
    description: "Mensagens marcadas pela validação anti-injection — auditoria de jailbreak, vazamento de prompt, off-topic",
    group: "IA"
  },
  {
    key: "sidebardefaults",
    label: "Ordem dos menus",
    icon: Settings,
    description: "Ordem default dos itens do menu lateral por portal (aluno/prof/org/admin) — quem não personalizou recebe essa ordem",
    group: "Configurações"
  },
  {
    key: "orphandefault",
    label: "Organização default",
    icon: ShieldQuestion,
    description: "Para onde vão alunos que se cadastram sem vínculo a uma organização específica",
    group: "Configurações"
  },
  {
    key: "studentvisibility",
    label: "Visibilidade no aluno",
    icon: Eye,
    description: "Esconde ou mostra partes do painel do aluno (rótulo de IA, webhooks)",
    group: "Configurações"
  },
  {
    key: "notifications",
    label: "Notificações",
    icon: Bell,
    description: "Catálogo de tipos de notificação cadastrados — toggle on/off, JSON exemplo, histórico. (Mensagens do aluno têm view própria fundida com notificações apenas no portal do aluno — no admin ficam separadas.)",
    group: "Comunicação"
  },
  {
    key: "userreports",
    label: "Bugs & Sugestões",
    icon: MessageSquareWarning,
    description: "Bug reports e feature requests enviados pelos usuários — triagem, status, severidade e resposta",
    group: "Operação"
  },
  {
    key: "adminalerts",
    label: "Alertas de incidentes",
    icon: Bell,
    description: "Liga/desliga emails automáticos de alerta pro admin (DB caiu, IA 5xx, gateway pagto fora). Sem deploy — vale em ~30s",
    group: "Operação"
  },
  {
    key: "faq",
    label: "FAQ",
    icon: HelpCircle,
    description: "Perguntas frequentes exibidas no portal do aluno — toggle global, add/edit/reorder e suporte a markdown",
    group: "Comunicação"
  },
  {
    key: "branding",
    label: "Identidade visual",
    icon: Palette,
    description: "Nome da plataforma, favicon, cores e labels dos portais — editáveis sem deploy",
    group: "Configurações"
  },
  {
    key: "legal",
    label: "Documentos legais",
    icon: FileText,
    description: "Termos de Uso, Política de Privacidade e Cookies — versionados; publicar dispara re-aceite de toda a base",
    group: "Configurações"
  },
  {
    key: "errorlog",
    label: "Monitoramento de erros",
    icon: Bug,
    description: "Quebras capturadas no front (browser) e no Worker (API), com stack e contexto",
    group: "Operação"
  },
  {
    key: "accountclosures",
    label: "Encerramentos de conta",
    icon: DoorOpen,
    description: "Pedidos de encerramento de conta — processar suspende o login (reversível)",
    group: "Operação"
  },
  {
    key: "extensionaccess",
    label: "Acesso à extensão",
    icon: Puzzle,
    description: "Controla quem pode gerar o token da extensão Chrome (off / allowlist / todos)",
    group: "Configurações"
  },
  {
    key: "payments",
    label: "Métodos de pagamento",
    icon: Wallet,
    description: "Quais métodos (Pix, cartão de crédito) estão disponíveis na plataforma",
    group: "Pagamentos"
  },
  {
    key: "planos",
    label: "Planos",
    icon: Crown,
    description: "Free, Pro e outros planos da plataforma — preço e recursos liberados",
    group: "Pagamentos"
  },
  {
    key: "assinaturas",
    label: "Assinaturas",
    icon: Repeat,
    description: "Todas as cobranças recorrentes (mensalidade da plataforma + pacotes de orgs)",
    group: "Pagamentos"
  },
  {
    key: "emailsettings",
    label: "Servidor de email",
    icon: Mail,
    description: "API Key, From e domínios verificados no Resend",
    group: "Comunicação"
  },
  {
    key: "emailtemplates",
    label: "Templates de email",
    icon: LayoutTemplate,
    description: "Templates transacionais (cobranças, recuperação de senha, etc)",
    group: "Comunicação"
  },
  {
    key: "emaillog",
    label: "Log de envios",
    icon: Mail,
    description: "Auditoria de cada email disparado (kind, destino, status, motivo)",
    group: "Comunicação"
  },
  {
    key: "featureflags",
    label: "Feature Flags",
    icon: FlagIcon,
    description: "Pilote POCs e rollout gradual por plataforma, usuários e grupos",
    group: "Operação"
  },
  {
    key: "analytics",
    label: "Analytics",
    icon: BarChart3,
    description: "DAU/WAU/MAU, charts e export XLS",
    group: "Operação"
  }
];

const SIDEBAR_COLLAPSED_KEY = "manager-new-sidebar-collapsed";
function initialSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

const activeSection = signal<SectionKey>("organizacoes");
const sidebarCollapsed = signal<boolean>(initialSidebarCollapsed());
const sidebarMobileOpen = signal<boolean>(false);
const themeSignal = signal<"light" | "dark">(getActiveTheme());

function toggleSidebar() {
  sidebarCollapsed.value = !sidebarCollapsed.value;
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed.value ? "1" : "0");
  } catch {
    // ignora
  }
}

// Toggle responsivo: desktop colapsa/expande largura; mobile abre/fecha drawer.
// Exposto pra main.tsx (Ctrl+/ Ctrl+\ Ctrl+B).
export function toggleSidebarResponsive() {
  if (typeof window === "undefined") return;
  if (window.matchMedia("(min-width: 768px)").matches) {
    toggleSidebar();
  } else {
    sidebarMobileOpen.value = !sidebarMobileOpen.value;
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("theme:changed", (e: any) => {
    themeSignal.value = e?.detail?.theme ?? getActiveTheme();
  });
}

type Phase = "loading" | "ready" | "error" | "forbidden";
const phase = signal<Phase>("loading");
const user = signal<UserSession | null>(null);
const activeContext = signal<ActiveContext | null>(null);
const availableContexts = signal<ActiveContext[]>([]);
const userProfiles = signal<Array<{ kind: string; role: string; companies: any[] }>>([]);
const errorMsg = signal<string | null>(null);

function isPlatformAdmin(ctx: ActiveContext | null): boolean {
  return !!ctx && ctx.kind === "platform" && ctx.role === "admin";
}

async function bootstrap() {
  phase.value = "loading";
  try {
    const res = await api.me();
    user.value = res.user;
    activeContext.value = res.activeContext;
    availableContexts.value = res.contexts;
    userProfiles.value = (res as any).profiles || [];
    // Tenta auto-trocar pro contexto platform/admin se o usuário tiver mas não
    // estiver nele. Conveniência: admin que volta da home não precisa repassar
    // pelo picker.
    if (!isPlatformAdmin(res.activeContext)) {
      const platformAdmin = res.contexts.find(
        (c) => c.kind === "platform" && c.role === "admin"
      );
      if (platformAdmin) {
        try {
          await api.switchContext({ kind: "platform", role: "admin" });
          activeContext.value = platformAdmin;
        } catch {
          /* segue pro forbidden */
        }
      }
    }
    if (!isPlatformAdmin(activeContext.value)) {
      phase.value = "forbidden";
      return;
    }
    // Carrega feature flags do user — não bloqueia o boot se falhar.
    void bootstrapFeatureFlags("platform");
    phase.value = "ready";
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errorMsg.value = friendlyError(err);
    phase.value = "error";
  }
}

async function doLogout() {
  try {
    await api.logout();
  } finally {
    window.location.href = "/auth/";
  }
}

// =====================
// Root
// =====================

export function App() {
  useEffect(() => {
    bootstrap();
  }, []);
  const ph = phase.value;
  if (ph === "loading") return <BootLoading />;
  if (ph === "error") return <BootError msg={errorMsg.value} />;
  if (ph === "forbidden") return <Forbidden />;
  return (
    <>
      <ReadyShell />
      <ConfirmRoot />
      <PromptRoot />
    </>
  );
}

function BootLoading() {
  return (
    <div class="grid min-h-screen place-items-center bg-background">
      <div class="flex items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="size-5 animate-spin text-primary" />
        Carregando Painel Admin…
      </div>
    </div>
  );
}

function BootError({ msg }: { msg: string | null }) {
  return (
    <div class="grid min-h-screen place-items-center bg-background p-4">
      <div class="max-w-md space-y-3 rounded-md border border-destructive/30 bg-destructive/5 px-6 py-8 text-center">
        <AlertCircle className="mx-auto size-8 text-destructive" />
        <h2 class="text-base font-bold">Falha ao carregar o painel</h2>
        {msg && <p class="text-sm text-muted-foreground">{msg}</p>}
        <button
          type="button"
          onClick={() => bootstrap()}
          class="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Tentar novamente
        </button>
      </div>
    </div>
  );
}

function Forbidden() {
  return (
    <div class="grid min-h-screen place-items-center bg-background p-4">
      <div class="max-w-md space-y-3 rounded-md border border-amber-500/40 bg-amber-500/5 px-6 py-8 text-center">
        <Shield className="mx-auto size-8 text-amber-600 dark:text-amber-400" />
        <h2 class="text-base font-bold">Acesso restrito</h2>
        <p class="text-sm text-muted-foreground">
          Esta área é exclusiva para administradores. Faça login com uma conta
          de admin pra continuar.
        </p>
        <button
          type="button"
          onClick={() => doLogout()}
          class="rounded-md bg-primary px-4 py-2 text-sm font-bold text-white hover:brightness-110"
        >
          Trocar de conta
        </button>
      </div>
    </div>
  );
}

// =====================
// Shell com sidebar + main
// =====================

function ReadyShell() {
  const collapsed = sidebarCollapsed.value;
  const mainOffset = collapsed ? "md:ml-16" : "md:ml-72";
  return (
    <div class="min-h-screen">
      <Sidebar />
      <div class={`flex min-h-screen min-w-0 flex-col transition-[margin] duration-200 ${mainOffset}`}>
        <Topbar />
        <main class="flex-1 px-5 py-6 md:px-8 md:py-8">
          <SectionRouter />
        </main>
      </div>
    </div>
  );
}

function Topbar() {
  return (
    <div class="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-background/80 px-5 py-3 backdrop-blur-md md:px-8">
      <button
        type="button"
        class="rounded-md border border-border bg-background px-2 py-1 md:hidden"
        onClick={() => (sidebarMobileOpen.value = true)}
        title="Abrir menu"
      >
        <Menu width="1em" height="1em" />
      </button>
      <Breadcrumbs />
    </div>
  );
}

interface CrumbItem {
  label: string;
  onClick?: () => void;
}

function Breadcrumbs() {
  const section = SECTIONS.find((s) => s.key === activeSection.value);
  const items: CrumbItem[] = [{ label: sectionLabel(section) }];
  return <BreadcrumbList items={items} />;
}

function BreadcrumbList({ items }: { items: CrumbItem[] }) {
  return (
    <nav class="flex min-w-0 items-center gap-1.5 text-sm" aria-label="Trilha">
      {items.map((c, i) => {
        const isLast = i === items.length - 1;
        return (
          <>
            {i > 0 && (
              <ChevronRight width="1.3em" height="1.3em" className="shrink-0 text-primary" />
            )}
            {c.onClick && !isLast ? (
              <button
                type="button"
                onClick={c.onClick}
                class="truncate rounded text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground"
              >
                {c.label}
              </button>
            ) : (
              <span
                class={`truncate ${
                  isLast ? "font-semibold text-foreground" : "text-muted-foreground"
                }`}
              >
                {c.label}
              </span>
            )}
          </>
        );
      })}
    </nav>
  );
}

function SectionRouter() {
  const key = activeSection.value;
  const meta = SECTIONS.find((s) => s.key === key);
  if (!meta) return null;
  return (
    <div class="space-y-5">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <meta.icon class="size-6 text-primary" />
          {sectionLabel(meta)}
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">{sectionDescription(meta)}</p>
      </header>
      {key === "recado" ? (
        <RecadoView />
      ) : key === "jogos" ? (
        <JogosConfigView />
      ) : key === "bancos" ? (
        <BancosView />
      ) : key === "library" ? (
        <LibraryExplorerView />
      ) : key === "essaythemes" ? (
        <EssayThemesView />
      ) : key === "usuarios" ? (
        <UsuariosView />
      ) : key === "referrals" ? (
        <ReferralsView />
      ) : key === "professores" ? (
        <ProfessoresView />
      ) : key === "grupos" ? (
        <GruposView />
      ) : key === "gruposestudo" ? (
        <GruposEstudoView />
      ) : key === "organizacoes" ? (
        <OrganizacoesView />
      ) : key === "billing" ? (
        <BillingView />
      ) : key === "credits" ? (
        <CreditsView />
      ) : key === "player" ? (
        <PlayerStationsView />
      ) : key === "aiconnections" ? (
        <AiConnectionsConfigView />
      ) : key === "aistudy" ? (
        <AiStudyAccessView />
      ) : key === "aifeatureconfig" ? (
        <AiFeatureConfigView />
      ) : key === "aiusersstatus" ? (
        <AiUsersStatusView />
      ) : key === "aiusage" ? (
        <AiUsageView />
      ) : key === "sidebardefaults" ? (
        <SidebarDefaultsView />
      ) : key === "notifications" ? (
        <NotificationsCatalogView />
      ) : key === "userreports" ? (
        <UserReportsView />
      ) : key === "faq" ? (
        <FaqView />
      ) : key === "branding" ? (
        <BrandingView />
      ) : key === "legal" ? (
        <LegalTermsView />
      ) : key === "errorlog" ? (
        <ErrorLogView />
      ) : key === "accountclosures" ? (
        <AccountClosuresView />
      ) : key === "extensionaccess" ? (
        <ExtensionAccessView />
      ) : key === "signup" ? (
        <SignupAllowlistView />
      ) : key === "payments" ? (
        <PaymentsConfigView />
      ) : key === "assinaturas" ? (
        <AssinaturasView />
      ) : key === "planos" ? (
        <PlanosView />
      ) : key === "creditsplans" ? (
        <CreditsPlansView />
      ) : key === "adminalerts" ? (
        <AdminAlertsView />
      ) : key === "emailsettings" ? (
        <EmailSettingsView />
      ) : key === "emailtemplates" ? (
        <EmailTemplatesView />
      ) : key === "emaillog" ? (
        <EmailLogView />
      ) : key === "featureflags" ? (
        <FeatureFlagsView />
      ) : key === "orphandefault" ? (
        <OrphanDefaultView />
      ) : key === "studentvisibility" ? (
        <StudentUiVisibilityView />
      ) : key === "aicreditsgate" ? (
        <AiCreditsGateView />
      ) : key === "quickstudyconfig" ? (
        <QuickStudyConfigView />
      ) : key === "quickstudyincidents" ? (
        <QuickStudyIncidentsView />
      ) : key === "paymentsandbox" ? (
        <PaymentSandboxView />
      ) : (
        <ComingSoonSection meta={meta} />
      )}
    </div>
  );
}

function ComingSoonSection({ meta }: { meta: SectionMeta }) {
  return (
    <div class="space-y-4">
      <div class="rounded-md border border-dashed border-border bg-card/40 px-5 py-8 text-center">
        <meta.icon class="mx-auto mb-3 size-10 text-muted-foreground/50" />
        <h2 class="text-base font-bold">Em construção</h2>
        <p class="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
          Esta seção está sendo portada do Painel Admin legado.
          A funcionalidade completa vai aparecer aqui em breve.
        </p>
      </div>

      {/* Skeleton de tabela genérica — só pra demonstrar o layout enquanto
          a seção real não chegou. Cada porting substitui isso. */}
      <div class="space-y-2 rounded-md border border-border bg-card/40 p-4">
        <div class="flex items-center justify-between">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-9 w-32" />
        </div>
        <Skeleton className="h-10 w-full" />
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    </div>
  );
}

// =====================
// Sidebar
// =====================

// Renderiza as SECTIONS agrupadas por section.group. Mantém a ordem de
// aparição dos grupos (primeira ocorrência ganha) e dos itens dentro do
// grupo. Quando o sidebar está colapsado não mostra o header — só um
// divisor sutil pra preservar o agrupamento visual sem ocupar largura.
function renderGroupedSections(collapsed: boolean) {
  const groups: Array<{ name: string; items: SectionMeta[] }> = [];
  const groupIndex = new Map<string, number>();
  for (const section of SECTIONS) {
    const g = section.group ?? "Geral";
    let idx = groupIndex.get(g);
    if (idx === undefined) {
      idx = groups.length;
      groupIndex.set(g, idx);
      groups.push({ name: g, items: [] });
    }
    groups[idx].items.push(section);
  }
  return groups.map((g, gi) => (
    <div key={g.name} class={gi === 0 ? "" : "pt-3"}>
      {!collapsed ? (
        <div class="mb-1 px-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
          {g.name}
        </div>
      ) : gi > 0 ? (
        <div class="my-2 border-t border-border/40" aria-hidden="true" />
      ) : null}
      <div class="space-y-1">
        {g.items.map((section) => (
          <NavLink
            key={section.key}
            section={section}
            isActive={activeSection.value === section.key}
            collapsed={collapsed}
            onClick={() => {
              activeSection.value = section.key;
              sidebarMobileOpen.value = false;
            }}
            activeKey={activeSection.value}
            onChildClick={(childKey) => {
              activeSection.value = childKey;
              sidebarMobileOpen.value = false;
            }}
          />
        ))}
      </div>
    </div>
  ));
}

function Sidebar() {
  const collapsed = sidebarCollapsed.value;
  const mobileOpen = sidebarMobileOpen.value;
  const widthClass = collapsed ? "md:w-16" : "md:w-72";
  const mobilePos = mobileOpen
    ? "translate-x-0"
    : "-translate-x-full md:translate-x-0";
  const sectionPadX = collapsed ? "px-2" : "px-4";

  return (
    <>
      {mobileOpen && (
        <div
          class="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={() => (sidebarMobileOpen.value = false)}
        />
      )}
      <aside
        class={`fixed inset-y-0 left-0 z-40 flex w-72 flex-col border-r border-border bg-card transition-all duration-200 ${widthClass} ${mobilePos}`}
      >
        <div
          class={`flex h-16 items-center border-b border-border ${sectionPadX} ${
            collapsed ? "justify-center" : "gap-2"
          }`}
        >
          <div class="grid size-9 shrink-0 place-items-center rounded-md bg-brand-gradient text-white shadow-sm ring-1 ring-white/10">
            <Shield width="1.2em" height="1.2em" strokeWidth={2.25} />
          </div>
          {!collapsed && (
            <>
              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-bold leading-tight">
                  {user.value?.name ?? user.value?.email ?? "—"}
                </div>
                <div class="truncate text-[11px] text-muted-foreground">
                  Painel Admin
                </div>
              </div>
              {/* Botão de colapsar — só desktop */}
              <button
                type="button"
                onClick={toggleSidebar}
                title="Recolher sidebar (Ctrl+/)"
                aria-label="Recolher sidebar"
                class="hidden size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:grid"
              >
                <PanelLeftClose className="size-4" />
              </button>
              {/* Botão de fechar drawer — só mobile */}
              <button
                type="button"
                onClick={() => (sidebarMobileOpen.value = false)}
                title="Fechar"
                aria-label="Fechar menu"
                class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:hidden"
              >
                <X className="size-4" />
              </button>
            </>
          )}
        </div>

        {/* Botão de expandir quando colapsado (desktop) */}
        {collapsed && (
          <div class="hidden border-b border-border px-2 py-2 md:block">
            <button
              type="button"
              onClick={toggleSidebar}
              title="Expandir sidebar (Ctrl+/)"
              aria-label="Expandir sidebar"
              class="grid h-9 w-full place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <PanelLeftOpen className="size-4" />
            </button>
          </div>
        )}

        <nav
          class={`min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain py-5 ${sectionPadX}`}
        >
          {renderGroupedSections(collapsed)}
        </nav>

        <div class={`space-y-1 border-t border-border py-4 ${sectionPadX}`}>
          <button
            type="button"
            onClick={() => {
              const next = toggleTheme();
              themeSignal.value = next;
            }}
            title={collapsed ? (themeSignal.value === "dark" ? "Tema claro" : "Tema escuro") : undefined}
            class={`flex w-full items-center rounded-md text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${
              collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5"
            }`}
          >
            {themeSignal.value === "dark" ? (
              <Sun width="1.3em" height="1.3em" />
            ) : (
              <Moon width="1.3em" height="1.3em" />
            )}
            {!collapsed && <span>Tema {themeSignal.value === "dark" ? "claro" : "escuro"}</span>}
          </button>
          <button
            type="button"
            onClick={() => {
              if ((userProfiles.value || []).length <= 1) {
                void openAlert({
                  title: "Único perfil",
                  message:
                    "Você só tem este perfil cadastrado — não há outro pra alternar."
                });
                return;
              }
              window.location.href = "/auth/?picker=1";
            }}
            title={collapsed ? "Trocar perfil" : undefined}
            class={`flex w-full items-center rounded-md text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${
              collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5"
            }`}
          >
            <ArrowLeftRight width="1.3em" height="1.3em" />
            {!collapsed && <span>Trocar perfil</span>}
          </button>
          <button
            type="button"
            onClick={() => doLogout()}
            title={collapsed ? "Sair" : undefined}
            class={`flex w-full items-center rounded-md text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive ${
              collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5"
            }`}
          >
            <LogOut width="1.3em" height="1.3em" />
            {!collapsed && <span>Sair</span>}
          </button>
        </div>
      </aside>
    </>
  );
}

function NavLink({
  section,
  isActive,
  collapsed,
  onClick,
  activeKey,
  onChildClick
}: {
  section: SectionMeta;
  isActive: boolean;
  collapsed: boolean;
  onClick: () => void;
  activeKey?: SectionKey;
  onChildClick?: (key: SectionKey) => void;
}) {
  const Icon = section.icon;
  const hasChildren = !!section.children && section.children.length > 0;
  const childActive = hasChildren && activeKey
    ? section.children!.some((c) => c.key === activeKey)
    : false;
  const [expanded, setExpanded] = useState<boolean>(() => {
    if (!hasChildren) return false;
    try {
      const raw = localStorage.getItem("sidebar.expanded.manager");
      const arr = raw ? (JSON.parse(raw) as unknown) : null;
      if (Array.isArray(arr) && arr.includes(section.key)) return true;
    } catch { /* ignore */ }
    return childActive;
  });
  useEffect(() => {
    if (childActive && !expanded) setExpanded(true);
  }, [childActive]);
  function toggleExpanded() {
    const next = !expanded;
    setExpanded(next);
    try {
      const raw = localStorage.getItem("sidebar.expanded.manager");
      const arr = raw ? (JSON.parse(raw) as unknown) : null;
      const set = new Set<string>(Array.isArray(arr) ? (arr as string[]) : []);
      if (next) set.add(section.key);
      else set.delete(section.key);
      localStorage.setItem("sidebar.expanded.manager", JSON.stringify(Array.from(set)));
    } catch { /* ignore */ }
  }

  return (
    <>
      <button
        type="button"
        onClick={hasChildren ? toggleExpanded : onClick}
        title={collapsed ? sectionLabel(section) : undefined}
        {...(hasChildren ? { "aria-expanded": expanded ? "true" : "false" } : {})}
        class={`flex w-full items-center rounded-md text-sm font-medium transition-colors ${
          collapsed ? "justify-center px-2 py-2.5" : "gap-2.5 px-3 py-2 text-left"
        } ${
          isActive || childActive
            ? "bg-brand-gradient text-white shadow-md shadow-primary/30 ring-1 ring-white/10"
            : "text-muted-foreground hover:bg-accent hover:text-foreground"
        }`}
      >
        <Icon width="1.3em" height="1.3em" className="shrink-0" strokeWidth={2} />
        {!collapsed && (
          <>
            <span class="flex-1 truncate">{sectionLabel(section)}</span>
            {hasChildren && (
              <ChevronDown
                width="1em"
                height="1em"
                class={`shrink-0 transition-transform ${expanded ? "" : "-rotate-90"}`}
              />
            )}
          </>
        )}
      </button>
      {hasChildren && expanded && !collapsed && (
        <div class="ml-3 mt-1 flex flex-col gap-0.5 border-l border-border pl-3">
          {section.children!.map((child) => {
            const ChildIcon = child.icon;
            const isChildActive = activeKey === child.key;
            return (
              <button
                key={child.key}
                type="button"
                onClick={() => onChildClick?.(child.key)}
                class={`flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors ${
                  isChildActive
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {ChildIcon && <ChildIcon width="1em" height="1em" className="shrink-0" />}
                <span class="flex-1 text-left truncate">{child.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}





